import {Model} from './engine.js';
import {Schema} from './schema.js';
import { Property, TypeKind } from './types.js';
import {typeSymbol} from './engine-impl.js';
import {OclEngine, OclExecutionContext, OclParser, PackageDeclaration} from '@canvas-logic/ocl';
import {DeepModelWalker} from './internal/ModelWalker.js';
import {PropertyValue} from './internal/PropertyPathWalker.js';
import {Utils} from "@canvas-logic/ocl";

class ExpressionConstraint {
  protected expression: ExpressionFunction = () => true;

  evaluate(model: Model, option?: any): boolean {
    return this.expression(model, option);
  }

  constructor(public errorMessage: string) {
  }
}

type ExpressionFunction = (model: any, option: any) => boolean;

export class JsExpressionConstraint extends ExpressionConstraint {
  constructor(expression: ExpressionFunction, errorMessage: string,) {
    super(errorMessage);
    this.expression = expression;
  }
}

export class OclExpressionConstraint extends ExpressionConstraint {
  private oclExpression: string;

  constructor(oclExpression: string, errorMessage: string, public schema: Schema) {
    super(errorMessage);
    let packageDeclaration: PackageDeclaration;
    this.oclExpression = oclExpression;
    try {
      packageDeclaration = OclEngine.Parser.parse(oclExpression, [], OclParser.registeredTypes);
    } catch (e) {
      e.oclExpression = oclExpression;
      throw e;
    }

    this.expression = (model: Model) => {
      //@todo find more elegant solution
      Utils.typeDeterminerFn = function(){
        return (model as any)[typeSymbol];
      };
      const visitor = new OclExecutionContext(model);
      visitor.registerStatics(this.schema.staticAPI);
      visitor.registerTypes(OclParser.registeredTypes);
      // TODO: Add enum support
      // visitor.setRegisteredEnumerations(this.registeredEnums);
      packageDeclaration.evaluate(visitor);
      if (visitor.getFailedInvariants().length > 0) {
        return false;
      }
      const walker = new DeepModelWalker(this.schema, model, (model as any)[typeSymbol]);
      let propValue: PropertyValue | undefined;
      while (propValue = walker.next()) {
        let type = this.schema.getType(propValue.property.type);
        if (type.kind == TypeKind.Object) {
          //@todo find more elegant solution
          Utils.typeDeterminerFn = function(){
            return propValue.property.type;
          };
          const visitor = new OclExecutionContext(propValue.value);
          visitor.registerStatics(this.schema.staticAPI);
          visitor.registerTypes(OclParser.registeredTypes);
          packageDeclaration.evaluate(visitor);
          if (visitor.getFailedInvariants().length > 0) {
            return false;
          }
        }
      }
      return true;
    };
  }
}

export function createExpressionConstraint(constraint: { jsExpression?: string, oclExpression?: string, errorMessage: string }, schema?: Schema): ExpressionConstraint | undefined {
  if (constraint.jsExpression) {
    //todo: new Function ?
    return new JsExpressionConstraint(eval(`(function () {
      return ${constraint.jsExpression};
    })()`), constraint.errorMessage);
  } else if (constraint.oclExpression && schema) {
    return new OclExpressionConstraint(constraint.oclExpression, constraint.errorMessage, schema);
  }
}

interface Constraint {
  name: string;

  valid(value: any): boolean
}


enum Constraints {
  MinLength   = 'MinLength',
  MaxLength   = 'MaxLength',
  Required    = 'Required',
  Pattern     = 'Pattern',
  NumberRange = 'NumberRange',
  UniqueItems = 'UniqueItems',
}


abstract class BaseConstraint implements Constraint {
  name: string;

  constructor(name: string) {
    this.name = name;
  }

  abstract valid(value: any): boolean

  // abstract compatibleTypes(): TypeReference[]

}

class MinLengthConstraint extends BaseConstraint {
  min: Number = 0;


  constructor() {
    super(Constraints.MinLength)
  }

  // compatibleTypes(): TypeReference[] {
  // 	return [];
  // }


  valid(value: String): boolean {
    return value.length < this.min;
  }
}

//
// class MaxLengthConstraint implements TypeConstraint {
// 	max: Number;
//
// 	valid(value: String): boolean {
// 		return value.length > this.max;
// 	}
//
// }
//
// class PatternConstraint implements Constraint<String, Type<String>> {
// 	pattern: RegExp;
//
//
// 	constructor(pattern: RegExp) {
// 		this.pattern = pattern;
// 	}
//
// 	valid(value: String): boolean {
// 		let matcher = value.match(this.pattern);
// 		return matcher != null &&  length != 0;
// 	}
// }
//

class RequiredConstraint extends BaseConstraint {

  constructor() {
    super(Constraints.Required);
  }

  valid(value: any): boolean {
    return value != null
  }
}

class UniqueConstraint extends BaseConstraint {

  constructor() {
    super(Constraints.UniqueItems);
  }

  valid(value: any): boolean {
    throw new Error('Not implemented yet!')
  }
}

class ConstraintInstance {
  name: string;
  kwargs: object;

  constructor(name: string, kwargs: object = {}) {
    this.name = name;
    this.kwargs = kwargs;
  }
}

export function isPropertyRequired(property: Property): boolean {
  return property.opts.constraints?.some(constraint => constraint.name.includes('Required')) ?? false;
}
export {
  MinLengthConstraint,
  RequiredConstraint,
  UniqueConstraint,
  Constraints,
  ConstraintInstance,
  ExpressionConstraint

}
